/**
 * Site VLANs + the site VLAN ROLLOUT -- server-state hooks and response types.
 *
 * Backend: api/v2/brand_config.py (/sites/{id}/vlans) and api/v2/site_vlan_rollout.py
 * (plan / apply / status, switch settings, gateway HA pair, per-port trunk overrides).
 * Contract: docs/design/site-vlan-rollout.md. Kept in its own module so the rollout's types do
 * not bloat hooks.ts; keys still come from the shared scope-prefixed queryKeys.
 *
 * Propose-then-confirm: the plan query is READ-ONLY and hands back ONE plan_token covering every
 * device; apply sends only that token and the server re-plans from live data, refusing if
 * anything moved. Writes (VLANs, settings, overrides, apply, verify) are msp_admin server-side.
 *
 * AUTO-DEPLOY (owner decision 2026-09-14): saving or deleting a site VLAN queues one automatic
 * rollout server-side (services/site_vlan_auto_deploy). useAutoDeploy polls its state every 2 s
 * while it is queued or running; the manual plan/apply hooks remain for the review dialog.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import { qk } from "./queryKeys";
import { useSession } from "@/stores/session";
import type { GatewayNetworkRow, GatewayNetworkTable, GatewayPlan } from "@/types/domain";
import { applyBody, planParams } from "@/lib/skipOffline";
import type { RolloutPathCheck, VlanPathCompact } from "@/lib/vlanPath";

/* ---- types ------------------------------------------------------------------------------ */

export type DhcpSource = "site" | "infoblox" | "none";

/** One row of GET /sites/{id}/vlans. */
export interface SiteVlanRow {
  id: number;
  site_id: number;
  vlan_id: number;
  vlan_name: string;
  purpose: string | null;
  dhcp_source: DhcpSource | null;
  dhcp_lease_time: string | null;
  subnet: string | null;
  gateway_ip: string | null;
  provenance: "template" | "site_edited" | "site";
  updated_at: string | null;
  updated_by: string | null;
}

export interface SiteVlanInput {
  vlan_id?: number;
  vlan_name: string;
  subnet: string | null;
  gateway_ip: string | null;
  dhcp_source: DhcpSource | null;
  dhcp_lease_time: string | null;
}

/** A refusal (blocks apply) or a warning (does not): stable code + operator text. */
export interface RolloutItem {
  code: string;
  message: string;
  serial: string | null;
  /** removed_vlan_in_use / removed_vlan_usage_unknown: the deleted VLAN (for "Keep on devices"). */
  vlan?: number;
  /**
   * switch_unreachable (an OFFLINE switch) only: the operator may waive it with "Skip offline
   * switches" (owner decision 2026-10-13). Every other refusal still blocks.
   */
  skippable?: boolean;
}

/** What an offline switch WOULD have received (plan.skipped_offline[].would_receive). */
export interface SkippedWouldReceive {
  /** Every site VLAN this run carries (tagged on its trunks). */
  vlans: number[];
  /** Of those, the ones not yet in its VLAN database (from its last report). */
  create_vlans: number[];
  /** Deleted site VLANs this run removes elsewhere. */
  remove_vlans: number[];
  /** ecCLI ports it would have tagged ("1/28"), from its LAST reported state. */
  trunk_ports: string[];
  commands: string[];
  /** False when it never reported a usable state: only the VLAN list is certain. */
  state_known: boolean;
  /** Other reasons it could not have been configured (informational: it is not contacted). */
  other_refusals: string[];
}

/** One offline switch a skip plan leaves out (GET .../plan?skip_offline=true). */
export interface SkippedOfflineSwitch {
  serial: string;
  name: string;
  model: string | null;
  reachability: string | null;
  would_receive: SkippedWouldReceive;
}

/**
 * A switch skipped while offline whose VLAN changes are still PENDING
 * (GET /sites/{id}/vlan-rollout/pending-switches, /devices/{serial}/vlan-rollout/pending).
 * Never applied automatically: the operator re-plans just that switch and applies it.
 */
export interface PendingSwitch {
  serial: string;
  name: string;
  model: string | null;
  online: boolean;
  /** Online and an ecCLI switch: the one-click re-plan is offered. */
  can_replan: boolean;
  pending_vlans: number[];
  pending_removals: number[];
  skipped_in: { rollout_id: number; at: string | null; acknowledged_by: string | null }[];
  last_skipped_at: string | null;
}

/** Plan options: skip offline switches, or re-plan ONE switch (scope). */
export interface PlanOptions {
  skipOffline?: boolean;
  /** Serial of the switch to re-plan alone (pending VLAN changes). */
  switchSerial?: string | null;
}

export interface RolloutNeighbor {
  class: "switch" | "gateway" | "ap" | "phone" | "foreign_switch" | "unknown";
  name: string | null;
  remote_port: string | null;
  label: string | null;
  match: string | null;
  chassis_mac: string | null;
  serial: string | null;
  kind: string | null;
  site_id: number | null;
}

export type TrunkOverrideMode = "trunk" | "exclude" | "ap";

export interface RolloutPortDecision {
  /** uCentral port name (zero-based) -- display through formatSwitchPort. */
  port: string;
  /** The ecCLI interface the commands address, e.g. "1/10". */
  eccli: string | null;
  carrier: boolean;
  override: TrunkOverrideMode | null;
  override_native_vlan: number | null;
  action: "trunk" | "untouched";
  reason: string;
  role: "trunk" | "ap" | null;
  native_target: number | null;
  native_current: number | null;
  neighbors: RolloutNeighbor[];
}

export interface RolloutSwitchPlan {
  serial: string;
  name: string;
  kind: "switch";
  model: string;
  host: string | null;
  online: boolean;
  vlan_db_existing: number[];
  create_vlans: { vlan: number; name: string | null }[];
  tagged_vlans: number[];
  ports: RolloutPortDecision[];
  trunk_ports: string[];
  commands: string[];
  warnings: RolloutItem[];
  refusals: RolloutItem[];
  /** Deleted site VLANs this switch still has: taken off its trunk ports and out of its database. */
  remove_vlans?: number[];
}

/** A deleted site VLAN still to be removed from the devices (GET .../vlan-auto-deploy). */
export interface PendingRemoval {
  vlan_id: number;
  vlan_name: string;
  subnet: string | null;
  gateway_ip: string | null;
  deleted_by: string | null;
  deleted_at: string | null;
}

/**
 * An existing UNADDRESSED same-VLAN network the rollout adopts instead of adding a new one
 * (services/gateway_config.site_vlan_adoptions). It gets the site VLAN's address, DHCP scope
 * and the LAN ports and keeps everything else (e.g. SSID blocks an old fan-out left on it).
 */
export interface GatewayAdoption {
  vlan: number;
  /** The name it ends up with: the site VLAN's, unless the config references the old name. */
  name: string;
  from_name: string;
  renamed: boolean;
  /** SSID blocks it carries and keeps. */
  ssids: string[];
  /** Server sentence, e.g. "adopts existing unaddressed network vlan1254 (VLAN 1254) as ...". */
  message: string;
  /** Its row after the change (address, subnet, DHCP). */
  network?: GatewayNetworkRow | null;
}

export interface RolloutGatewayPlan {
  serial: string;
  name: string;
  kind: "gateway";
  role: "primary" | "secondary" | "single";
  model: string;
  online: boolean;
  config_uuid: number | null;
  changed: boolean;
  added: GatewayNetworkRow[];
  /** Networks of deleted site VLANs this gateway drops in the same push. */
  removed?: GatewayNetworkRow[];
  /** Existing unaddressed same-VLAN networks adopted (NOT listed in `added` / `removed`). */
  adopted?: GatewayAdoption[];
  /**
   * Site VLANs already on this gateway whose 802.1Q EGRESS TAGGING this push repairs — they
   * were configured and addressed but left the box untagged, so they carried nothing.
   * Neither an add nor a removal, so they are listed on their own.
   */
  retagged?: { vlan: number; name: string | null }[];
  skipped: { vlan: number; reason: string }[];
  diff: GatewayPlan["diff"];
  warnings: RolloutItem[];
  refusals: RolloutItem[];
  networks_before: GatewayNetworkTable | null;
  networks_after: GatewayNetworkTable | null;
}

export interface HaPairView {
  primary: { serial: string; name: string } | null;
  secondary: { serial: string; name: string } | null;
  group_name: string | null;
  addressing: {
    vip_host: number;
    physical_hosts: Record<string, number>;
    dhcp_ranges: Record<string, number[]>;
  };
}

export interface SwitchSettings {
  site_id: number;
  ap_native_vlan: number | null;
  ap_native_served_upstream: boolean;
  updated_by: string | null;
  updated_at: string | null;
}

export interface VlanRolloutPlan {
  site_id: number;
  site_name: string;
  plan_token: string;
  can_apply: boolean;
  vlans: { vlan_id: number; vlan_name: string; subnet: string | null }[];
  /** Deleted site VLANs this plan removes. */
  removals?: PendingRemoval[];
  ha_pair: HaPairView | null;
  switch_settings: SwitchSettings;
  apply_order: string[];
  switches: RolloutSwitchPlan[];
  gateways: RolloutGatewayPlan[];
  refusals: RolloutItem[];
  warnings: RolloutItem[];
  /** Offline switches at the site (with skip off they are in `switches` with a skippable refusal). */
  offline_switches?: { serial: string; name: string }[];
  /** True when this plan was asked to skip offline switches. */
  skip_offline?: boolean;
  /** Offline switches left out of this plan, with what each would have received. */
  skipped_offline?: SkippedOfflineSwitch[];
  /** Set on a one-switch re-plan. */
  scope?: { switch: string } | null;
  /**
   * Switches still flagged "pending VLAN changes" that this SITE-WIDE plan leaves alone (owner
   * decision 2026-10-14): manual runs, edit-triggered auto-deploys and drift converge never
   * configure them -- only "Re-plan this switch" does, and only that clears the flag.
   */
  held_pending_replan?: HeldPendingSwitch[];
  /** OP#1818: the VLAN path check per planned VLAN (hops this plan configures are skipped in
   *  `warnings`; a SonicFi switch the rollout cannot touch shows up as vlan_path_unknown). */
  path_check?: Record<string, VlanPathCompact>;
  /**
   * INFO notes, never acknowledged (owner decision 2026-10-14): e.g. `vlan_path_unknown` -- a
   * hop that cannot be checked (a SonicFi switch until OP#1820). A PROVEN broken hop is a warning.
   */
  notes?: RolloutItem[];
}

/** One switch a site-wide plan holds for its re-plan (plan.held_pending_replan[]). */
export interface HeldPendingSwitch {
  serial: string;
  name: string;
  model?: string | null;
  online: boolean;
  pending_vlans: number[];
  pending_removals: number[];
  would_receive: SkippedWouldReceive;
}

/**
 * `skipped` = not attempted after an earlier device failed. `skipped_offline` = the operator
 * chose to skip it while offline (acknowledged by name); it is flagged "pending VLAN changes".
 */
export type RolloutDeviceStatus =
  | "pending"
  | "running"
  | "applied"
  | "failed"
  | "skipped"
  | "skipped_offline"
  /** A pending switch this site-wide run left alone; only its re-plan configures it. */
  | "held_pending_replan";

export interface RolloutDeviceResult {
  serial: string;
  name: string;
  kind: "switch" | "gateway";
  role: string | null;
  status: RolloutDeviceStatus;
  error: string | null;
  detail: {
    no_op?: boolean;
    applied?: string[];
    partial?: boolean;
    saved?: boolean | null;
    /** skipped_offline rows: what it missed and who acknowledged skipping it. */
    would_receive?: SkippedWouldReceive;
    acknowledged_by?: string | null;
    acknowledged_at?: string | null;
    /** held_pending_replan rows: what is still pending on it. */
    pending_vlans?: number[];
    pending_removals?: number[];
  } | null;
  /** Set on the applied row of a one-switch re-plan (the only run that clears a pending flag). */
  replan?: boolean;
  no_op: boolean;
  /**
   * Switches only: the trunk ports this run tagged (services/site_vlan_rollout.start_rollout
   * stores it per device). Empty means the run created the VLANs in the database but tagged NO
   * uplinks -- so an "applied" status is NOT evidence the VLANs reached a trunk. See
   * switchVlanStatus, which refuses to claim "on its uplinks" from such a run.
   */
  trunk_ports?: string[];
}

export interface RolloutVerificationDevice {
  serial: string;
  name?: string;
  kind: "switch" | "gateway";
  /**
   * `awaiting_state`: a gateway whose newest state document predates its push (not evidence
   * yet). `not_attempted`: the automatic read-back after a FAILED run skips devices the run
   * did not apply (OP#1822).
   */
  status:
    | "verified"
    | "not_observed"
    | "no_state"
    | "cli_error"
    | "missing"
    | "error"
    | "awaiting_state"
    | "not_attempted"
    | "skipped_offline"
    | "held_pending_replan";
  error?: string | null;
  vlans?: { vlan: number; expected: string; observed: boolean }[];
  /** Switches: site VLANs in its VLAN database. */
  present?: number[];
  missing_vlans?: number[];
  /** Switches: {uCentral port: site VLANs that trunk port does not carry tagged}. */
  missing_tagged?: Record<string, number[]>;
  ports?: { port: string; missing_tagged: number[]; native_ok: boolean; native_expected: number | null }[];
  /** skipped_offline / held_pending_replan: the VLANs it did not get from this run. */
  pending_vlans?: number[];
}

/** One switch the read-back found missing a site VLAN (verification.missing). */
export interface RolloutMissingSwitch {
  serial: string;
  name: string;
  missing_vlans: number[];
  missing_tagged: Record<string, number[]>;
}

/**
 * The read-back of a run. Since OP#1822 it runs AUTOMATICALLY at the end of every run that
 * reached its devices (`trigger: "auto"`); "Verify on devices" re-runs it (`"manual"`).
 */
export interface RolloutVerification {
  checked_at: string;
  /** verified | verified_except_skipped | incomplete | error */
  status: string;
  trigger?: "auto" | "manual";
  /** Serials skipped while offline: reported "skipped (offline)", never "missing". */
  skipped_offline?: string[];
  /** Serials held for their re-plan: also never read back and never "missing". */
  held_pending_replan?: string[];
  devices: RolloutVerificationDevice[];
  missing?: RolloutMissingSwitch[];
  /** Set when the automatic read-back itself crashed (status "error"). */
  error?: string;
  /** OP#1818: the end-to-end VLAN path check over stored reads, per rolled-out VLAN. */
  path_check?: RolloutPathCheck;
}

export interface VlanRollout {
  id: number;
  site_id: number;
  /** `refused` = an automatic run whose plan had refusals: nothing was applied. */
  status: "running" | "succeeded" | "failed" | "interrupted" | "refused";
  /** Who started it: the review dialog (manual) or auto-deploy after a site VLAN change. */
  trigger?: "manual" | "auto";
  vlan_ids: number[];
  /** Deleted site VLANs this run removed from devices. */
  removed_vlan_ids?: number[];
  devices: RolloutDeviceResult[];
  /** Serials this run skipped while offline (derived from devices). */
  skipped_offline?: string[];
  /** Serials this run held for their re-plan (derived from devices). */
  held_pending_replan?: string[];
  verification: RolloutVerification | null;
  /** A refused run's refusals (empty otherwise). */
  refusals?: RolloutItem[];
  error: string | null;
  started_by: string | null;
  started_at: string | null;
  finished_at: string | null;
}

export type AutoDeployState = "idle" | "queued" | "running" | "succeeded" | "failed" | "refused";

/** GET /sites/{id}/vlan-auto-deploy -> {auto_deploy}. */
export interface AutoDeployView {
  site_id: number;
  enabled: boolean;
  state: AutoDeployState;
  due_at: string | null;
  /** Server-computed, so a skewed browser clock cannot show a wrong countdown. */
  seconds_until_due: number | null;
  requested_by: string | null;
  requested_at: string | null;
  /** A change saved while a deploy runs: one more deploy starts right after. */
  follow_up_queued: boolean;
  /** The newest rollout of any trigger. */
  last_run: VlanRollout | null;
  pending_removals: PendingRemoval[];
  quiet_seconds: number;
  updated_by: string | null;
}

/** The `auto_deploy` block a site VLAN create/update/delete returns. */
export interface AutoDeployAck {
  enabled: boolean;
  queued: boolean;
  due_at: string | null;
}

/* ---- site VLAN rows ----------------------------------------------------------------------- */

function useScope() {
  const orgId = useSession((s) => s.currentOrgId);
  return orgId;
}

/** GET /sites/{id}/vlans */
export function useSiteVlans(siteId: string) {
  const orgId = useScope();
  return useQuery({
    queryKey: qk.sites.siteVlans(orgId, siteId),
    queryFn: ({ signal }) =>
      api.get<{ items: SiteVlanRow[]; count: number }>(`/sites/${siteId}/vlans`, undefined, signal),
    enabled: !!orgId && !!siteId,
  });
}

/** Invalidate everything a site-level change can move (VLAN list, plan, auto-deploy state). */
function useInvalidateSite(siteId: string) {
  const qc = useQueryClient();
  const orgId = useScope();
  return () => {
    void qc.invalidateQueries({ queryKey: qk.sites.siteVlans(orgId, siteId) });
    void qc.invalidateQueries({ queryKey: qk.sites.vlanRolloutPlan(orgId, siteId) });
    // A save or delete queues a deploy: the strip must show "Deploying…" without a reload.
    void qc.invalidateQueries({ queryKey: qk.sites.vlanAutoDeploy(orgId, siteId) });
  };
}

/** POST /sites/{id}/vlans (create) or PUT /sites/{id}/vlans/{vlan_id} (edit). Queues a deploy. */
export function useSaveSiteVlan(siteId: string) {
  const invalidate = useInvalidateSite(siteId);
  return useMutation({
    mutationFn: (vars: { editVlanId: number | null; body: SiteVlanInput }) =>
      vars.editVlanId == null
        ? api.post<SiteVlanRow & { auto_deploy: AutoDeployAck | null }>(`/sites/${siteId}/vlans`, vars.body)
        : api.put<SiteVlanRow & { auto_deploy: AutoDeployAck | null }>(
            `/sites/${siteId}/vlans/${vars.editVlanId}`,
            vars.body,
          ),
    onSuccess: invalidate,
  });
}

/**
 * DELETE /sites/{id}/vlans/{vlan_id}. Queues a deploy that REMOVES the VLAN from every device.
 * 409 {code: vlan_in_use, uses[]} while something at the site still uses it.
 */
export function useDeleteSiteVlan(siteId: string) {
  const invalidate = useInvalidateSite(siteId);
  return useMutation({
    mutationFn: (vlanId: number) =>
      api.del<{ deleted: number; auto_deploy: AutoDeployAck | null }>(`/sites/${siteId}/vlans/${vlanId}`),
    onSuccess: invalidate,
  });
}

/* ---- auto-deploy -------------------------------------------------------------------------- */

/** GET /sites/{id}/vlan-auto-deploy -- polled every 2 s while a deploy is queued or running. */
export function useAutoDeploy(siteId: string) {
  const orgId = useScope();
  return useQuery({
    queryKey: qk.sites.vlanAutoDeploy(orgId, siteId),
    queryFn: ({ signal }) =>
      api.get<{ auto_deploy: AutoDeployView }>(`/sites/${siteId}/vlan-auto-deploy`, undefined, signal),
    enabled: !!orgId && !!siteId,
    refetchInterval: (query) => {
      const state = query.state.data?.auto_deploy?.state;
      return state === "queued" || state === "running" ? 2000 : false;
    },
  });
}

/** Write the returned view into the cache and refresh the plan (devices may be about to change). */
function useAutoDeployWrite<TVars>(siteId: string, fn: (vars: TVars) => Promise<{ auto_deploy: AutoDeployView }>) {
  const qc = useQueryClient();
  const orgId = useScope();
  return useMutation({
    mutationFn: fn,
    onSuccess: (res) => {
      qc.setQueryData(qk.sites.vlanAutoDeploy(orgId, siteId), res);
      void qc.invalidateQueries({ queryKey: qk.sites.vlanRolloutPlan(orgId, siteId) });
    },
  });
}

/** PUT /sites/{id}/vlan-auto-deploy {enabled}. Turning it on queues a deploy. msp_admin. */
export function useSetAutoDeploy(siteId: string) {
  return useAutoDeployWrite(siteId, (enabled: boolean) =>
    api.put<{ auto_deploy: AutoDeployView }>(`/sites/${siteId}/vlan-auto-deploy`, { enabled }),
  );
}

/** POST /sites/{id}/vlan-auto-deploy/retry -- the only re-run besides a new change. msp_admin. */
export function useRetryAutoDeploy(siteId: string) {
  return useAutoDeployWrite(siteId, () =>
    api.post<{ auto_deploy: AutoDeployView }>(`/sites/${siteId}/vlan-auto-deploy/retry`, {}),
  );
}

/** DELETE /sites/{id}/vlan-removals/{vlan_id} -- keep a deleted VLAN on the devices. msp_admin. */
export function useDropRemoval(siteId: string) {
  return useAutoDeployWrite(siteId, (vlanId: number) =>
    api.del<{ auto_deploy: AutoDeployView }>(`/sites/${siteId}/vlan-removals/${vlanId}`),
  );
}

/* ---- rollout ------------------------------------------------------------------------------ */

/**
 * GET /sites/{id}/vlan-rollout/plan -- READ-ONLY. Only fetched once the operator asks to review.
 * `opts`: skip offline switches, or re-plan one switch. The DEFAULT plan keeps the shared cache
 * key the Applied column uses; an option plan gets its own entry under the same prefix (so the
 * site-level invalidations still refresh it).
 */
export function useVlanRolloutPlan(siteId: string, enabled: boolean, opts?: PlanOptions) {
  const orgId = useScope();
  const params = planParams(opts);
  return useQuery({
    queryKey: params
      ? [...qk.sites.vlanRolloutPlan(orgId, siteId), params]
      : qk.sites.vlanRolloutPlan(orgId, siteId),
    queryFn: ({ signal }) =>
      api.get<VlanRolloutPlan>(`/sites/${siteId}/vlan-rollout/plan`, params, signal),
    enabled: enabled && !!orgId && !!siteId,
    // A plan describes live devices: never serve a cached one as if it were current.
    staleTime: 0,
    retry: false,
  });
}

/** GET /sites/{id}/vlan-rollout/status -- polled every 2 s while a rollout is running. */
export function useVlanRolloutStatus(siteId: string) {
  const orgId = useScope();
  return useQuery({
    queryKey: qk.sites.vlanRolloutStatus(orgId, siteId),
    queryFn: ({ signal }) =>
      api.get<{ rollout: VlanRollout | null }>(`/sites/${siteId}/vlan-rollout/status`, undefined, signal),
    enabled: !!orgId && !!siteId,
    refetchInterval: (query) => (query.state.data?.rollout?.status === "running" ? 2000 : false),
  });
}

/** Apply variables: the token alone, or a skip / one-switch apply. */
export interface ApplyVars {
  planToken: string;
  skipOffline?: boolean;
  /** Serials of EVERY skipped switch, each ticked by the operator (409 skip_ack_required otherwise). */
  acknowledgeSkipped?: string[];
  switchSerial?: string | null;
}

/** POST /sites/{id}/vlan-rollout/apply {plan_token[, skip_offline, acknowledge_skipped, switch]} -> 202. */
export function useApplyVlanRollout(siteId: string) {
  const qc = useQueryClient();
  const orgId = useScope();
  return useMutation({
    mutationFn: (vars: string | ApplyVars) =>
      api.post<{ rollout: VlanRollout }>(`/sites/${siteId}/vlan-rollout/apply`, applyBody(vars)),
    onSuccess: (res) => {
      qc.setQueryData(qk.sites.vlanRolloutStatus(orgId, siteId), res);
      void qc.invalidateQueries({ queryKey: qk.sites.vlanRolloutStatus(orgId, siteId) });
      // A skip or a one-switch run changes which switches are pending.
      void qc.invalidateQueries({ queryKey: qk.sites.vlanPendingSwitches(orgId, siteId) });
    },
  });
}

/** GET /sites/{id}/vlan-rollout/pending-switches -- switches skipped offline, VLANs still pending. */
export function usePendingSwitches(siteId: string) {
  const orgId = useScope();
  return useQuery({
    queryKey: qk.sites.vlanPendingSwitches(orgId, siteId),
    queryFn: ({ signal }) =>
      api.get<{ items: PendingSwitch[]; count: number }>(
        `/sites/${siteId}/vlan-rollout/pending-switches`,
        undefined,
        signal,
      ),
    enabled: !!orgId && !!siteId,
    // "Back online" is a device state change: re-read now and then so the re-plan offer appears.
    refetchInterval: 60_000,
  });
}

/** GET /devices/{serial}/vlan-rollout/pending -- this switch's pending VLAN changes, or null. */
export function useDevicePendingVlans(serial: string, enabled = true) {
  const orgId = useScope();
  return useQuery({
    queryKey: qk.sites.devicePendingVlans(orgId, serial),
    queryFn: ({ signal }) =>
      api.get<{ pending: PendingSwitch | null; site_id?: number }>(
        `/devices/${serial}/vlan-rollout/pending`,
        undefined,
        signal,
      ),
    enabled: enabled && !!orgId && !!serial,
  });
}

/** GET /sites/{id}/vlan-rollout/status?verify=true -- reads every device back (CLI + owgw). */
export function useVerifyVlanRollout(siteId: string) {
  const qc = useQueryClient();
  const orgId = useScope();
  return useMutation({
    mutationFn: () =>
      api.get<{ rollout: VlanRollout | null }>(`/sites/${siteId}/vlan-rollout/status`, { verify: true }),
    onSuccess: (res) => qc.setQueryData(qk.sites.vlanRolloutStatus(orgId, siteId), res),
  });
}

/* ---- site switch settings, HA pair, per-port overrides ------------------------------------ */

export function useSwitchSettings(siteId: string) {
  const orgId = useScope();
  return useQuery({
    queryKey: qk.sites.switchSettings(orgId, siteId),
    queryFn: ({ signal }) =>
      api.get<{ switch_settings: SwitchSettings }>(`/sites/${siteId}/switch-settings`, undefined, signal),
    enabled: !!orgId && !!siteId,
  });
}

export function useSaveSwitchSettings(siteId: string) {
  const qc = useQueryClient();
  const orgId = useScope();
  const invalidate = useInvalidateSite(siteId);
  return useMutation({
    mutationFn: (body: { ap_native_vlan: number | null; ap_native_served_upstream: boolean }) =>
      api.put<{ switch_settings: SwitchSettings }>(`/sites/${siteId}/switch-settings`, body),
    onSuccess: (res) => {
      qc.setQueryData(qk.sites.switchSettings(orgId, siteId), res);
      invalidate();
    },
  });
}

export function useGatewayHaPair(siteId: string) {
  const orgId = useScope();
  return useQuery({
    queryKey: qk.sites.gatewayHaPair(orgId, siteId),
    queryFn: ({ signal }) =>
      api.get<{ ha_pair: HaPairView | null }>(`/sites/${siteId}/gateway-ha-pair`, undefined, signal),
    enabled: !!orgId && !!siteId,
  });
}

/** PUT the pair, or DELETE it when `body` is null. */
export function useSaveGatewayHaPair(siteId: string) {
  const qc = useQueryClient();
  const orgId = useScope();
  const invalidate = useInvalidateSite(siteId);
  return useMutation({
    mutationFn: (body: { primary_serial: string; secondary_serial: string } | null) =>
      body
        ? api.put<{ ha_pair: HaPairView | null }>(`/sites/${siteId}/gateway-ha-pair`, body)
        : api.del<{ ha_pair: HaPairView | null }>(`/sites/${siteId}/gateway-ha-pair`),
    onSuccess: (res) => {
      qc.setQueryData(qk.sites.gatewayHaPair(orgId, siteId), res);
      invalidate();
    },
  });
}

/** PUT /devices/{serial}/switch/ports/{port}/trunk-override -- mode null clears it. */
export function useSetTrunkOverride(siteId: string) {
  const invalidate = useInvalidateSite(siteId);
  return useMutation({
    mutationFn: (vars: { serial: string; port: string; mode: TrunkOverrideMode | null }) =>
      api.put<unknown>(
        `/devices/${vars.serial}/switch/ports/${encodeURIComponent(vars.port)}/trunk-override`,
        { mode: vars.mode },
      ),
    onSuccess: invalidate,
  });
}
